import Button from "components/Buttons/Button";
import Render from "components/Common/Render";
import { useCallback, useState } from "react";
import {
  CheckmarkCircle20Filled,
  DismissCircleFilled,
  Info20Filled,
} from "@fluentui/react-icons";
import { PlanActivationStatus } from "enum/common";
import { amountOptions } from "constants/amountOptions";
import moment from "moment";
import useMixpanel from "hooks/useMixpanel";
import ListYourBusinessPromotionDetails from "components/Home/PromotionPlans/ListYourBusinessPromotionDetails";
import AcceptInstantPayPromotionDetails from "components/Home/PromotionPlans/AcceptInstantPayPromotionDetails";
import OffersPromotionDetails from "components/Home/PromotionPlans/OffersPromotionDetails";
import DoubleUpPromotionDetails from "components/Home/PromotionPlans/DoubleUpPromotionDetails";
import RegisteredBusinessPlans from "components/Home/RegisteredBusinessPlans";

interface IPromotionPlansProps {
  activateOffer: (
    planId: string,
    topUpAmount: number,
    referralCode: string
  ) => void;
  deActivateOffer: (planId: string) => void;
  updateTopupAmount: (planId: string, topUpAmount: number) => void;
  plans: BusinessPlanType[];
  isGlobal?: boolean;
  isUpdatingAmount?: boolean;
  isAnyWhereCommunity?: boolean;
  business:BusinessType;
  errorMessage:string;
  setErrorMessage:(value:string) => void;
}

const PromotionPlans: React.FC<IPromotionPlansProps> = ({
  activateOffer,
  deActivateOffer,
  updateTopupAmount,
  plans,
  isGlobal = true,
  isUpdatingAmount,
  isAnyWhereCommunity,
  business,
  errorMessage,
  setErrorMessage
}) => {
  const [topUpAmount, setTopupAmount] = useState(
    (!!plans?.length && plans[0]?.formData?.autoTopUpAmount) ||
      amountOptions[0]?.value
  );
  const [referralCode, setReferralCode] = useState(
    business.usedReferralCode || (!!plans?.length && plans[0]?.formData?.referralCode) || ""
  );
  const { trackEvent } = useMixpanel();
  const [allowTopupUpdate, setAllowTopupUpdate] = useState(false);

  const getDefaultValue = useCallback(() => {
    const defaultAmount =
      !!plans?.length && plans[0]?.formData?.autoTopUpAmount;
    return (
      amountOptions?.filter((amount) => amount?.value === defaultAmount)[0] ||
      amountOptions[0]
    );
  }, [topUpAmount, plans?.length]);

  const [showTopuError, setShowTopupError] = useState(false);
  const renderOfferStatus = (status: PlanActivationStatus) => {

    switch (status) {
      // Not using This Status When status is INITIATED Will show NOT ACTIVE.
      // case PlanActivationStatus.INITIATED:
      //   return (
      //     <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
      //       <CheckmarkCircle20Filled color="green" className="mr-1 mb-[2px]" />
      //       <span className="text-black opacity-60"> Status: </span>
      //       <span className="text-green font-medium">Initiated</span>
      //     </div>
      //   );
      case PlanActivationStatus.ACTIVATED:
        return (
          <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
            <CheckmarkCircle20Filled color="green" className="mr-1 mb-[2px]" />
            <span className="text-black opacity-60"> Status: </span>
            <span className="text-green font-medium">Active</span>
          </div>
        );
        case PlanActivationStatus.PENDING:
          return (
            <div className="text-darkBlue text-base flex gap-1 flex-row items-center">
              <CheckmarkCircle20Filled color="orange" className="mr-1 mb-[2px]" />
              <span className="text-black opacity-60"> Status: </span>
              <span className="text-yellow font-medium">Pending</span>
            </div>
          );
      case PlanActivationStatus.FAILED:
        return (
          <div className="text-darkBlue flex-1">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">FAILED</span>
          </div>
        );
      case PlanActivationStatus.EXPIRED:
        return (
          <div className="text-darkBlue flex-1 ">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">Not Active</span>
          </div>
        );
      case PlanActivationStatus.DE_ACTIVATED:
        return (
          <div className="text-darkBlue flex-1 ">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">Not Active</span>
          </div>
        );
      default:
        return (
          <div className="text-darkBlue flex-1 ">
            <DismissCircleFilled className="mr-1 mb-[2px] text-failed" />
            <span className="text-black">Status: </span>
            <span className="text-failed font-medium">Not Active</span>
          </div>
        );
    }
  };

  const RenderPromotionalDetailsContainer = (plan: BusinessPlanType) => {
    if (plan?.module.label === "List your business on Open Rewards") {
      return (
        <ListYourBusinessPromotionDetails
          plan={plan}
          isGlobal={isGlobal}
          allowTopupUpdate={allowTopupUpdate}
          getDefaultValue={getDefaultValue}
          setTopupAmount={setTopupAmount}
          setReferralCode={setReferralCode}
          referralCode={referralCode}
          setShowTopupError={setShowTopupError}
          error={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      );
    } else if (plan?.module.label === "Accept Instant Pay") {
      return <AcceptInstantPayPromotionDetails />;
    } else if (plan?.module.label === "Offers") {
      return <OffersPromotionDetails />;
    } else if (plan?.module.label === "Double-up") {
      return <DoubleUpPromotionDetails />;
    }
  };

  const handleUpdateAmount = (planId: string = "") => {
    trackEvent("update_button_Clicking", { planId, topUpAmount });
    if (topUpAmount > 0 && allowTopupUpdate) {
      updateTopupAmount(planId || "", topUpAmount);
      setAllowTopupUpdate(false);
    } else {
      setAllowTopupUpdate(true);
      setShowTopupError(true);
      setTopupAmount(0);
    }
  };

  const handleActivateOffer = (plan: BusinessPlanType) => {
    if (plan?.module.label === "List your business on Open Rewards") {
      trackEvent("activate_offer_button_Clicking", {
        plan: plan?.uuid,
        topUpAmount,
      });
      if (topUpAmount > 0) {
        activateOffer(plan?.uuid || "", topUpAmount, referralCode);
      } else {
        setShowTopupError(true);
      }
    } else if (plan?.module.label === "Accept Instant Pay") {
      // NO-OP
    } else if (plan?.module.label === "Offers") {
      // NO-OP
    } else if (plan?.module.label === "Double-up") {
      // NO-OP
    }
  };

  const isFreeTrialActive = (trialActive?: boolean, trialEndDate?: string) => {
    if (!trialActive) return false;

    const currentDate = moment();
    const trialEnd = moment(trialEndDate);
    return trialActive || !trialEnd.isBefore(currentDate);
  };

  const filteredPlans = () => {
    // for business belonging to Anywhere Community, we will show all the plans
    // but for thw business belonging to Community, we will not show "List your business on Open Rewards"
    // plan as that is by default active for all the businesses
    return isAnyWhereCommunity
      ? plans
      : plans?.filter(
          (plan) => plan?.module?.label !== "List your business on Open Rewards"
        );
  };

  const getClassName = (plan: BusinessPlanType) => {
    return isFreeTrialActive(plan?.module?.isActive, plan?.module?.trialEndDate)
      ? "line-through"
      : "";
  };

  return (
    <div className="">
      {/* showing List your business card active for the registered community businesses */}
      {!isAnyWhereCommunity && (
        <div className="mb-4">
          <RegisteredBusinessPlans  businessStatus={business?.businessStatus} />
        </div>
      )}

      {filteredPlans()?.map((plan) => (
        <div
          key={plan?.uuid}
          className="bg-white  p-4 mb-3 gap-4 flex flex-col rounded-sm "
        >
          <div className="flex md:flex-row flex-col"></div>
          <div className="flex flex-col md:flex-row ">
            <div className="flex flex-row flex-1 md:mb-0 mb-4 items-center">
              <div className="flex-col">
                <div className="text-navyBlue text-xl font-semibold flex-1">
                  {" "}
                  {plan?.module?.label}
                </div>
                {plan?.module?.isActive && (
                  <div className="flex-1 mt-1">
                    {renderOfferStatus(plan?.status)}
                  </div>
                )}
              </div>
            </div>
            {plan?.module?.isActive && (
              <div className="flex flex-col">
                <span className="text-navyBlue font-semibold text-xl ">
                  <span className={getClassName(plan)}>
                    ${plan?.module?.price}
                  </span>
                  <span className="text-navyBlue font-semibold text-sm">
                    {" "}
                    {`/month`}
                  </span>
                </span>{" "}
                {isFreeTrialActive(
                  plan?.module?.trialActive,
                  plan?.module?.trialEndDate
                ) &&
                  plan?.module?.trialEndDate && (
                    <div className="text-yellow">
                      <Info20Filled className="mr-1 mb-[3px]" />
                      <span className="font-semibold text-l">
                        FREE until{" "}
                        {moment
                          .utc(plan?.module?.trialEndDate)
                          .format("Do MMM YYYY")}
                      </span>
                    </div>
                  )}
              </div>
            )}
          </div>

          {RenderPromotionalDetailsContainer(plan)}

          <Render when={isGlobal}>
            <div className="flex justify-end md:flex-row flex-col">
              <Render when={plan?.status === PlanActivationStatus.ACTIVATED  }>
                <Button
                  label={allowTopupUpdate ? "Save" : "Update"}
                  type={"button"}
                  loading={isUpdatingAmount}
                  customClass={"md:!w-auto"}
                  onClick={() => handleUpdateAmount(plan?.uuid)}
                />
                <Button
                  label={"Deactivate"}
                  type={"button"}
                  variant="secondary"
                  disabled={plan?.status !== PlanActivationStatus.ACTIVATED}
                  customClass={
                    "md:!w-auto !bg-failed !bg-white !text-failed !border-failed"
                  }
                  onClick={() => deActivateOffer(plan?.uuid || "")}
                />
              </Render>
              <Render when={plan?.status !== PlanActivationStatus.ACTIVATED  }>
                <Button
                  label={plan?.module?.isActive ? "Activate" : "COMING SOON!"}
                  type={"button"}
                  disabled={
                    plan?.status === PlanActivationStatus.ACTIVATED || plan?.status === PlanActivationStatus.PENDING|| 
                    !plan?.module?.isActive
                  }
                  customClass={"md:!w-auto"}
                  onClick={() => {
                    handleActivateOffer(plan);
                  }}
                />
              </Render>
            </div>
          </Render>
        </div>
      ))}
    </div>
  );
};

export default PromotionPlans;
